import { Children } from 'react'

export const noop = () => {}
export const identity = x => x
export const sum = (a, b) => a + b
export const range = (from, to) =>
  Array.from(new Array(to - from)).map((a, i) => i + from)
export const isNullOrUndefined = value => value === null || value === undefined
export const isEmptyObject = value =>
  typeof value === 'object' && Object.keys(value).length === 0
export const isEmpty = val => {
  if (val instanceof Map || val instanceof Set) {
    return val.size === 0
  }
  if (!isNullOrUndefined(val) && !isNullOrUndefined(val.length)) {
    return val.length === 0
  }
  if (typeof val === 'object') {
    return isEmptyObject(val)
  }
  throw new TypeError('value must be object, array-like, map or set.')
}
export const isNullOrUndefinedOrEmpty = value => {
  if (isNullOrUndefined(value)) return true
  try {
    return isEmpty(value)
  } catch (e) {}
  return false
}
export const isPresent = value => !isNullOrUndefinedOrEmpty(value)
export const equalTo = compare => value => value === compare
export const isSetEqual = (a, b) => {
  const set1 = new Set(a || [])
  const set2 = new Set(b || [])
  const compareSet = new Set([...set1, ...set2])
  return compareSet.size === set1.size && compareSet.size === set2.size
}
export const arrayEquals = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((val, index) => val === b[index])
export const not =
  func =>
  (...args) =>
    !func(...args)
export const oneOf = compares => value => compares.some(equalTo(value))
export const mapValues = mapper => target =>
  Object.entries(target).reduce(
    (result, [key, value]) => ({
      ...result,
      [key]: mapper(value, key, target),
    }),
    {}
  )
export const shallowArrayEquals = (arr1, arr2) => {
  if (isNullOrUndefined(arr1) || isNullOrUndefined(arr2)) return false
  if (arr1.length !== arr2.length) return false
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false
  }
  return true
}
export const filterEntries = filter => target =>
  Object.entries(target)
    .filter(([key, value], index, entries) =>
      filter(key, value, index, entries)
    )
    .reduce(
      (result, [key, value]) => ({
        ...result,
        [key]: value,
      }),
      {}
    )

export const reduceEntries = (obj, [key, value]) => ({
  ...obj,
  [key]: value,
})

export const fillProps = from => mapValues((value, key) => from[key])
export const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

export const resolveAsync = responseDelay => value =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), responseDelay)
  })
export const rejectAsync =
  (responseDelay, getHandler = () => identity) =>
  value =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        Promise.resolve(getHandler()(value)).then(newError =>
          reject(newError === undefined ? value : newError)
        )
      }, responseDelay)
    })
export const resolveAxiosAsync = value =>
  new Promise(resolve => {
    setTimeout(() => resolve({ data: value }), 500)
  })
export const ignoreThrow =
  func =>
  (...args) => {
    try {
      const possiblePromise = func(...args)
      return typeof possiblePromise.catch === 'function'
        ? possiblePromise.catch(noop)
        : possiblePromise
    } catch (ignore) {}
  }

export const wrapResult = async (promise, alternative) => {
  try {
    return [await promise]
  } catch (error) {
    return [alternative, error]
  }
}

export const isValidIp = ip => {
  const MAX_IP_LENGTH = 256
  const VALID_IP_PART_COUNT = 4
  const validIpParts = ip
    .split('.')
    .filter(
      t =>
        t === '*' || (t === Number(t).toString() && Number(t) < MAX_IP_LENGTH)
    )
  return validIpParts.length === VALID_IP_PART_COUNT
}

export const isValidUrl = (text = '') => {
  try {
    if (!/^https?:\/\/[^/\s]+/.test(text)) {
      return false
    }
    const { pathname, search } = new URL(text)
    if (pathname.includes('://')) {
      return false
    }
    if (search.includes('%')) {
      return (
        (search.match(/%/g) || []).length ===
        (search.match(/%[0-9A-Fa-f]{2}/g) || []).length
      )
    }
    return true
  } catch (e) {
    return false
  }
}

export const findClosestAncestor = condition =>
  function findWithCondition(start, end = document.documentElement) {
    if (isNullOrUndefined(start)) return null
    if (condition(start)) return start
    if (start === end) return null
    return findWithCondition(start.parentElement, end)
  }

export const isTag =
  tagName =>
  (element = {}) =>
    element.tagName &&
    element.tagName.toLowerCase &&
    element.tagName.toLowerCase() === tagName.toLowerCase()

export const separateUrlProtocol = url =>
  ((url || '').match(/(^https?):\/\/*(.*)/) || []).slice(1)

export const debounce = (func, delay, immediate) => {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    if (immediate) func(...args)
    timeout = setTimeout(() => func(...args), delay)
  }
}

export const invalidNumberInput = (value, max, min, step) =>
  value > max || value < min || value % step > 0
export const boundary = (value, max, min) => Math.min(Math.max(value, min), max)

export const premiumLinkRegex = /^[ㄱ-ㅎㅏ-ㅣ가-힣0-9a-z&\-+#./%@$'"=~:?*]+$/
export const talkChannelRegex = /^[ㄱ-ㅎㅏ-ㅣ가-힣0-9A-Za-z&\-+#./%@$'"=~:?*]+$/
export const alternativeKeywordRegex =
  /^[ㄱ-ㅎㅏ-ㅣ가-힣0-9a-zA-Z&\-+#./%@$'"=~:?*]+$/
export const siteNameRegex = /^[ㄱ-ㅎㅏ-ㅣ가-힣0-9a-zA-Z㈜()\-_[\]\s]+$/
export const whitespaceOnlyRegex = /^\s+$/
export const notWhitespaceBeginning = /^[^\s].+/
export const EmptyComponent = () => null

export const byteToMb = (byte, fractionDigits) =>
  (byte / 1024 / 1024).toFixed(fractionDigits)
export const byteToKb = (byte, fractionDigits) =>
  (byte / 1024).toFixed(fractionDigits)
export const mbToByte = mb => mb * 1024 * 1024
export const kbToByte = kb => kb * 1024

export const getFlattenObjectValueArray = obj => {
  const result = []
  const helper = (o, res) => {
    Object.keys(o).forEach(key => {
      if (typeof o[key] === 'object') {
        helper(o[key], res)
      } else {
        res.push(o[key])
      }
    })
    return res
  }
  return helper(obj, result)
}

export const numberSeparatedWithComma = n =>
  n.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const serializeError = error => {
  const {
    isAxiosError,
    response,
    name,
    type,
    message,
    code,
    errorCode,
    stack,
  } = error
  const newVar = {
    name,
    type,
    message,
    code,
    stack,
    errorCode: isAxiosError && response ? response.status : errorCode || code,
  }
  return filterEntries((key, value) => value !== undefined)(newVar)
}

export const convertToRealError = error =>
  error.message && error.stack
    ? Object.assign(new Error(error.message), { stack: error.stack })
    : error

export const getFieldRemoved =
  (...fieldNames) =>
  obj =>
    Object.entries(obj)
      .filter(([_key]) => !fieldNames.some(equalTo(_key)))
      .reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: value,
        }),
        {}
      )

export const flatMapChildrenFilteredByType = (children, type) =>
  Children.toArray(children).flatMap(child =>
    child.type === type
      ? child
      : flatMapChildrenFilteredByType(child.props.children, type)
  )

export const invoke = func => func()

export const sumByField = map =>
  map.reduce(
    (a, b) =>
      Object.keys(b).reduce(
        (result, key) => ({
          ...result,
          [key]: (a[key] || 0) + (b[key] || 0),
        }),
        {}
      ),
    {}
  )

export const limit = (number, minOrMax1, minOrMax2) => {
  const [min, max] =
    minOrMax1 < minOrMax2 ? [minOrMax1, minOrMax2] : [minOrMax2, minOrMax1]
  return Math.min(Math.max(min, number), max)
}

export const getBoundGap = (boundaryElement, movingElement) => {
  if (isNullOrUndefined(boundaryElement) || isNullOrUndefined(movingElement)) {
    return {
      minX: Number.MIN_SAFE_INTEGER,
      maxX: Number.MAX_SAFE_INTEGER,
      minY: Number.MIN_SAFE_INTEGER,
      maxY: Number.MAX_SAFE_INTEGER,
    }
  }

  const {
    top: boundaryTop,
    left: boundaryLeft,
    bottom: boundaryBottom,
    right: boundaryRight,
  } = boundaryElement.getBoundingClientRect()

  const { top, left, bottom, right } = movingElement.getBoundingClientRect()

  return {
    minX: boundaryLeft - left,
    maxX: boundaryRight - right,
    minY: boundaryTop - top,
    maxY: boundaryBottom - bottom,
  }
}

export const isInBound = (element, x, y) => {
  if (
    isNullOrUndefined(element) ||
    isNullOrUndefined(element.getBoundingClientRect)
  )
    return false
  const { top, left, bottom, right } = element.getBoundingClientRect()
  return left < x && x < right && top < y && y < bottom
}

export const partialClone = (obj = {}, props = []) =>
  Object.keys(obj)
    .filter(i => props.indexOf(i) > -1)
    .map(i => [i, obj[i]])
    .reduce((p, c) => Object.assign(p, { [c[0]]: c[1] }), {})

export const stopPropagation =
  (func = noop) =>
  e => {
    e.stopPropagation()
    func(e)
  }

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const tempElement = document.createElement('a')
  tempElement.href = url
  tempElement.setAttribute('download', filename)
  tempElement.click()
}
export const downloadCsvFile = (data, filename) => {
  const blob = new Blob([`\ufeff${data}`], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, filename)
}
export const getClipboardData = event =>
  event.clipboardData.getData('text/plain')
export const pasteAsText = text => {
  document.execCommand('insertText', false, text)
}

// css transition 동작을 위해, DOM 속성을 읽어서 브라우저 렌더링 쓰레드와 JS 엔진을 강제로 동기화
export const forceStyleSync = el => noop(el.offsetLeft)

export const round = (number, ndigit) =>
  Math.round(number * Math.pow(10, ndigit)) * Math.pow(10, -ndigit)
export const ceil = (number, ndigit) =>
  Math.ceil(number * Math.pow(10, ndigit)) * Math.pow(10, -ndigit)
export const floor = (number, ndigit) =>
  Math.floor(number * Math.pow(10, ndigit)) * Math.pow(10, -ndigit)

export const removeNumber = text => text.replace(/[^0-9]/g, '')

export const oppositeSortOrder = order => (order === 'desc' ? 'asc' : 'desc')

export const splitArrayBySize = (arr, size) =>
  range(0, Math.ceil(arr.length / size)).map(i =>
    arr.slice(i * size, i * size + size)
  )

export const increaseByPercent = (v, percentage) =>
  ceil(Number(v * (1 + percentage / 100)), -1)
export const decreaseByPercent = (v, percentage) =>
  floor(Number(v * (1 - percentage / 100)), -1)
export const integerValue = value =>
  value === '' ? value : parseInt(value, 10)

export const trimValue = value =>
  whitespaceOnlyRegex.test(value) || !notWhitespaceBeginning.test(value)
    ? value.trim()
    : value
export const hashCode = str =>
  Array.from(str).reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0)

export const dateformat = 'YYYY-MM-DD HH:mm'
export const isURIComponentEncoded = text => {
  try {
    return text !== decodeURIComponent(text)
  } catch (e) {
    return false
  }
}
export const isProduction = ['production', 'cbt'].includes(
  process.env.REACT_APP_DEPLOY_PHASE
)
export const getPathAdAccountId = () => {
  const splits = window.location.pathname.split('/')
  return splits.length > 1 && !isNaN(Number(splits[1]))
    ? Number(splits[1])
    : undefined
}
export const filterEmptyChildNodes = childNodes =>
  [...childNodes]
    .map(item => item.textContent.replaceAll(' ', ' '))
    .filter(item => !isEmpty(item.trim()))
export const getEventFiles = e => {
  const files = []
  if (e.dataTransfer.items) {
    for (const item of e.dataTransfer.items) {
      if (item.kind === 'file') {
        files.push(item.getAsFile())
      }
    }
  } else {
    for (const item of e.dataTransfer.files) {
      files.push(item)
    }
  }
  return files
}
